import React, { useState, useEffect, useContext, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stepper,
    Step,
    StepLabel,
    CircularProgress,
    Typography,
    TextField,
    Box,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import RequestService from '../../services/RequestService';
import { QrReader } from 'react-qr-reader';
import TableComponent from '../../widgets/TableComponent';

const CardAssociationDialog = ({ open, onClose, onSuccess }) => {
    const { user } = useContext(UserContext);

    const [activeStep, setActiveStep] = useState(0);

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);

    const [searchName, setSearchName] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [scanning, setScanning] = useState(false);
    const [cardCode, setCardCode] = useState(null);
    const [assigning, setAssigning] = useState(false);
    const [hasScanned, setHasScanned] = useState(false);

    const qrReaderRef = useRef(null);

    const resetDialogState = () => {
        setActiveStep(0);
        setSelectedUser(null);
        setSearchName('');
        setDebouncedSearch('');
        setScanning(false);
        setCardCode(null);
        setAssigning(false);
        setHasScanned(false);
        setUsers([]);
        setFilteredUsers([]);
    };

    const stopCamera = () => {
        if (qrReaderRef.current) {
            qrReaderRef.current.stopStream();
        }
        const video = document.querySelector('video');
        if (video && video.srcObject) {
            const tracks = video.srcObject.getTracks();
            tracks.forEach((track) => track.stop());
            video.srcObject = null;
        }
    };

    const handleClose = () => {
        stopCamera();
        resetDialogState();
        onClose();
    };

    useEffect(() => {
        if (open) {
            fetchUsers();
        }
    }, [open]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(searchName);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchName]);

    useEffect(() => {
        if (debouncedSearch !== '') {
            fetchUsers(debouncedSearch);
        }
    }, [debouncedSearch]);

    const fetchUsers = async (nameFilter = '') => {
        try {
            setLoadingUsers(true);
            const usersData = await RequestService.getUsersByCompany(
                user.company.id,
                nameFilter
            );
            setUsers(Array.isArray(usersData) ? usersData : []);
            setFilteredUsers(Array.isArray(usersData) ? usersData : []);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleSearchChange = (event) => {
        setSearchName(event.target.value);
    };

    const handleSelectUser = (selectedRow) => {
        setSelectedUser(selectedRow);
        setActiveStep(1);
        setHasScanned(false);
    };

    const handleScan = (result) => {
        if (result && !hasScanned) {
            try {
                const parsedData = JSON.parse(result?.text || '{}');

                if (parsedData.cardCode) {
                    setCardCode(parsedData.cardCode);
                    setScanning(false);
                    setActiveStep(2);
                    setHasScanned(true);
                }
            } catch (error) {
                console.error('Erro ao processar QR Code:', error);
            }
        }
    };

    useEffect(() => {
        if (activeStep !== 1) {
            setScanning(false);
            setHasScanned(false);
        }
    }, [activeStep]);

    const handleAssignCard = async () => {
        if (!selectedUser || !cardCode) return;

        setAssigning(true);
        try {
            await RequestService.assignCardToUser(cardCode, selectedUser.id);
            onSuccess();
            handleClose();
        } catch (error) {
            alert('Erro ao associar cartão!');
            console.error(error);
        } finally {
            setAssigning(false);
        }
    };

    const columns = [
        { label: 'ID', field: 'id' },
        { label: 'Nome', field: 'name' },
        { label: 'Apelido', field: 'nickname' },
        {
            label: 'Ações',
            field: 'actions',
            renderCell: (_, row) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleSelectUser(row)}
                >
                    Selecionar
                </Button>
            ),
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            keepMounted={false}
        >
            <DialogTitle>Associar Cartão</DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} alternativeLabel>
                    <Step>
                        <StepLabel>Selecionar Usuário</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Ler Cartão</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Confirmar</StepLabel>
                    </Step>
                </Stepper>

                {/* Step 1: Select User */}
                {activeStep === 0 && (
                    <>
                        <TextField
                            label="Buscar usuário"
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            value={searchName}
                            onChange={handleSearchChange}
                        />

                        {loadingUsers ? (
                            <CircularProgress />
                        ) : (
                            <Box sx={{ mt: 2 }}>
                                <TableComponent
                                    columns={columns}
                                    rows={filteredUsers}
                                />
                            </Box>
                        )}
                    </>
                )}

                {/* Step 2: Scan QR Code */}
                {activeStep === 1 && (
                    <>
                        <Typography variant="subtitle1">
                            Escaneie o QR Code do cartão:
                        </Typography>
                        {scanning ? (
                            <QrReader
                                ref={qrReaderRef}
                                constraints={{ facingMode: 'environment' }}
                                onResult={handleScan}
                                onError={(err) =>
                                    console.error('Erro no scanner:', err)
                                }
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <Button
                                variant="contained"
                                onClick={() => setScanning(true)}
                            >
                                Iniciar Scanner
                            </Button>
                        )}
                    </>
                )}

                {/* Step 3: Confirm Association */}
                {activeStep === 2 && (
                    <>
                        <Typography variant="h6">Confirmação</Typography>
                        <Typography>
                            <strong>Usuário:</strong> {selectedUser?.name} (
                            {selectedUser?.nickname})
                        </Typography>
                        <Typography>
                            <strong>Código do Cartão:</strong> {cardCode}
                        </Typography>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {activeStep > 0 && (
                    <Button
                        onClick={() => setActiveStep(activeStep - 1)}
                        disabled={assigning}
                    >
                        Voltar
                    </Button>
                )}
                {activeStep === 2 ? (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAssignCard}
                        disabled={assigning}
                    >
                        {assigning ? 'Atribuindo...' : 'Confirmar'}
                    </Button>
                ) : (
                    activeStep < 2 && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setActiveStep(activeStep + 1)}
                            disabled={!selectedUser && activeStep === 0}
                        >
                            Próximo
                        </Button>
                    )
                )}
                <Button onClick={handleClose} disabled={assigning}>
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CardAssociationDialog;
